<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  apiErrorMessage,
  useDeviceTypes,
} from '@/composables/equipment/useEquipmentLookups';
import { EquipmentService } from '@/services/equipment.services';
import { typeIcon } from '@/utils/equipmentIcons';
import type {
  DeviceStatus,
  IDevice,
  IDeviceBody,
} from '@/types/equipment.types';
import {
  emptyToNull,
  portToNumber,
  resetForType,
  settingsToBody,
  settingsToForm,
} from '@/utils/equipmentForm';
import EquipmentField from './EquipmentField.vue';
import EquipmentModal from './EquipmentModal.vue';
import EquipmentSelect from './EquipmentSelect.vue';
import EquipmentSiteSelect from './EquipmentSiteSelect.vue';
import EquipmentStoreSelect from './EquipmentStoreSelect.vue';

const props = defineProps<{ device?: IDevice; lockedStoreId?: string }>();
const emit = defineEmits<{ close: []; saved: [] }>();

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const { types, typeOf } = useDeviceTypes();
const editing = computed(() => !!props.device);

const STATUSES: DeviceStatus[] = ['planned', 'installed', 'retired'];

const form = reactive({
  storeId: props.device?.store.id ?? props.lockedStoreId ?? '',
  siteId: props.device?.site.id ?? '',
  type: props.device?.type ?? '',
  label: props.device?.label ?? '',
  role: props.device?.role ?? '',
  status: (props.device?.status ?? 'installed') as DeviceStatus,
  mac: props.device?.mac ?? '',
  location: props.device?.location ?? '',
  ip: props.device?.ip ?? '',
  port: props.device?.port ? String(props.device.port) : '',
  settings: {} as Record<string, string>,
});
const busy = ref(false);
const error = ref('');

const current = computed(() => typeOf(form.type));
const settingDefs = computed(() => current.value?.settings ?? []);
const showMac = computed(() => !!current.value && current.value.mac !== 'none');

// Les réglages en place n'existent qu'une fois le catalogue chargé : on les lit à ce moment-là.
watch(
  current,
  (type) => {
    if (!type || Object.keys(form.settings).length) return;
    form.settings = settingsToForm(
      type.settings,
      props.device?.settings ?? null,
      !editing.value,
    );
  },
  { immediate: true },
);

const onTypeChange = () => {
  const next = resetForType(current.value, form);
  Object.assign(form, {
    role: next.role,
    mac: next.mac,
    ip: next.ip,
    port: next.port,
  });
  form.settings = next.settings;
};
const onStoreChange = () => {
  form.siteId = '';
};

const typeOptions = computed(() =>
  types.value.map((item) => ({
    value: item.type,
    label: item.label,
    icon: typeIcon(item.type),
  })),
);
const roleOptions = computed(() => [
  { value: '', label: t('equipment.noRole') },
  ...(current.value?.roles ?? []).map((r) => ({
    value: r.role,
    label: r.label,
  })),
]);
const statusOptions = computed(() =>
  STATUSES.map((value) => ({
    value: value as string,
    label: t(`equipment.status.${value}`),
  })),
);

const canSave = computed(
  () => !!form.siteId && !!form.type && !!form.label.trim() && !busy.value,
);

const save = async () => {
  if (!canSave.value) return;
  busy.value = true;
  error.value = '';
  const networked = current.value?.networked ?? false;
  const body: IDeviceBody = {
    type: form.type,
    label: form.label.trim(),
    role: emptyToNull(form.role),
    status: form.status,
    mac: showMac.value ? emptyToNull(form.mac) : null,
    location: emptyToNull(form.location),
    ip: networked ? emptyToNull(form.ip) : null,
    port: networked ? portToNumber(form.port) : null,
    settings: settingsToBody(settingDefs.value, form.settings),
  };
  try {
    if (props.device)
      await EquipmentService.updateDevice(
        props.device.store.id,
        props.device.site.id,
        props.device.id,
        { ...body, siteId: form.siteId },
      );
    else await EquipmentService.createDevice(form.storeId, form.siteId, body);
    await queryClient.invalidateQueries({ queryKey: ['equipment'] });
    emit('saved');
  } catch (failure) {
    error.value = apiErrorMessage(failure, t('equipment.errors.generic'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <EquipmentModal
    wide
    :title="
      editing ? t('equipment.devices.edit') : t('equipment.devices.create')
    "
    @close="emit('close')"
  >
    <div class="dfm__row">
      <EquipmentField :label="t('equipment.fields.store')">
        <EquipmentStoreSelect
          v-model="form.storeId"
          :disabled="editing || !!lockedStoreId"
          @update:model-value="onStoreChange"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.site')">
        <EquipmentSiteSelect v-model="form.siteId" :store-id="form.storeId" />
      </EquipmentField>
    </div>

    <div class="dfm__row">
      <EquipmentField :label="t('equipment.fields.type')">
        <EquipmentSelect
          v-model="form.type"
          :options="typeOptions"
          :placeholder="t('equipment.chooseType')"
          :search-above="0"
          @update:model-value="onTypeChange"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.deviceLabel')">
        <input
          v-model="form.label"
          class="eq-input"
          type="text"
          maxlength="80"
        />
      </EquipmentField>
    </div>

    <div v-if="current" class="dfm__row">
      <EquipmentField :label="t('equipment.fields.role')">
        <EquipmentSelect
          v-model="form.role"
          :options="roleOptions"
          :search-above="0"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.status')">
        <EquipmentSelect
          v-model="form.status"
          :options="statusOptions"
          :search-above="0"
        />
      </EquipmentField>
    </div>

    <div v-if="current" class="dfm__row">
      <EquipmentField
        v-if="showMac"
        :label="t('equipment.fields.mac')"
        :hint="
          current.mac === 'expected'
            ? t('equipment.macExpected')
            : t('equipment.macOptional')
        "
      >
        <input
          v-model="form.mac"
          class="eq-input eq-mono"
          type="text"
          placeholder="aa:bb:cc:dd:ee:ff"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.location')">
        <input
          v-model="form.location"
          class="eq-input"
          type="text"
          maxlength="80"
        />
      </EquipmentField>
    </div>

    <div v-if="current?.networked" class="dfm__row">
      <EquipmentField
        :label="t('equipment.fields.ip')"
        :hint="t('equipment.ipHint')"
      >
        <input
          v-model="form.ip"
          class="eq-input eq-mono"
          type="text"
          placeholder="192.168.1.10"
        />
      </EquipmentField>
      <EquipmentField
        v-if="current.port !== null"
        :label="t('equipment.fields.port')"
        :hint="t('equipment.portHint')"
      >
        <input
          v-model="form.port"
          class="eq-input eq-mono"
          type="text"
          inputmode="numeric"
        />
      </EquipmentField>
    </div>

    <fieldset v-if="settingDefs.length" class="dfm__settings">
      <legend>{{ t('equipment.fields.settings') }}</legend>
      <div class="dfm__row">
        <EquipmentField
          v-for="def in settingDefs"
          :key="def.key"
          :label="def.label"
        >
          <EquipmentSelect
            v-if="def.values?.length"
            v-model="form.settings[def.key]"
            :options="[
              { value: '', label: '—' },
              ...def.values.map((value) => ({ value, label: value })),
            ]"
            :search-above="0"
          />
          <EquipmentSelect
            v-else-if="def.kind === 'boolean'"
            v-model="form.settings[def.key]"
            :options="[
              { value: '', label: '—' },
              { value: 'true', label: t('equipment.yes') },
              { value: 'false', label: t('equipment.no') },
            ]"
            :search-above="0"
          />
          <input
            v-else
            v-model="form.settings[def.key]"
            class="eq-input"
            :type="
              def.kind === 'number' || def.kind === 'integer'
                ? 'number'
                : 'text'
            "
            :maxlength="def.maxLength ?? undefined"
          />
        </EquipmentField>
      </div>
    </fieldset>

    <p v-if="error" class="eq-error" role="alert">{{ error }}</p>
    <template #footer>
      <button type="button" class="eq-btn" @click="emit('close')">
        {{ t('equipment.cancel') }}
      </button>
      <button
        type="button"
        class="eq-btn eq-btn--primary"
        :disabled="!canSave"
        @click="save"
      >
        {{ t('equipment.save') }}
      </button>
    </template>
  </EquipmentModal>
</template>

<style scoped>
.dfm__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 0.75rem;
}
.dfm__settings {
  margin: 0;
  padding: 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
}
.dfm__settings legend {
  padding: 0 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}
</style>
